/**
 * Strategem projectile / overlay FX: keep battlefield HP at pre-hit values until damage pop + countdown.
 */

export function shouldHoldHealthForStrategemAnimation(anim) {
  if (!anim || anim.targetType !== 'unit' || anim.effectType === 'heal') {
    return false;
  }
  const ghost = anim.ghostUnit;
  if (!ghost?.instanceId || typeof ghost.health !== 'number' || ghost.health <= 0) {
    return false;
  }
  return true;
}

/** True when unit has a shield that will block incoming strategem damage (matches Shield.hasBlockingShield). */
export function unitHasBlockingShieldForStrategemFx(unit) {
  if (!unit || unit.hasShield === false) {
    return false;
  }
  if (unit.hasShield === true) {
    return true;
  }
  return Array.isArray(unit.abilities) && unit.abilities.includes('Shield');
}

/**
 * Whether a unit-targeting strategem hit is fully blocked by an active shield.
 * Uses pre-hit ghost snapshot first, then live unit at projectile impact.
 */
export function isStrategemShieldDeflectedHit({
  unitSnapshot = null,
  liveUnit = null,
  damageAmount = null,
  healthBefore = null,
}) {
  if (typeof damageAmount !== 'number' || damageAmount <= 0) {
    return false;
  }
  if (
    unitHasBlockingShieldForStrategemFx(unitSnapshot)
    || unitHasBlockingShieldForStrategemFx(liveUnit)
  ) {
    return true;
  }
  if (
    unitSnapshot?.hasShield === true
    && liveUnit?.hasShield === false
    && typeof healthBefore === 'number'
    && typeof liveUnit.health === 'number'
    && liveUnit.health === healthBefore
  ) {
    return true;
  }
  return false;
}

/**
 * Attach pre/post HP metadata at strategem registration (before two-phase effect applies).
 */
export function enrichStrategemUnitDamageMeta(anim, damageAmount) {
  if (!shouldHoldHealthForStrategemAnimation(anim)) {
    return anim;
  }
  const healthBefore = anim.healthBefore ?? anim.ghostUnit.health;
  const targetInstanceId = anim.targetInstanceId ?? anim.ghostUnit.instanceId;
  if (typeof damageAmount !== 'number' || damageAmount <= 0) {
    return { ...anim, healthBefore, targetInstanceId };
  }
  if (
    isStrategemShieldDeflectedHit({
      unitSnapshot: anim.ghostUnit,
      damageAmount,
      healthBefore,
    })
  ) {
    return {
      ...anim,
      targetInstanceId,
      healthBefore,
      healthAfter: healthBefore,
      damageAmount: 0,
      shieldDeflected: true,
    };
  }
  return {
    ...anim,
    targetInstanceId,
    healthBefore,
    healthAfter: Math.max(0, healthBefore - damageAmount),
    damageAmount,
  };
}

/**
 * Resolve post-hit HP for survivor countdown when server state may still be pre-hit at projectile impact.
 */
export function resolveSurvivorHealthAfter({
  healthBefore,
  healthAfter,
  damageAmount,
  liveHealth,
}) {
  let resolvedAfter = healthAfter;
  if (
    typeof liveHealth === 'number'
    && typeof healthBefore === 'number'
    && liveHealth < healthBefore
  ) {
    resolvedAfter = liveHealth;
  } else if (resolvedAfter == null && typeof liveHealth === 'number') {
    resolvedAfter = liveHealth;
  }
  if (
    typeof healthBefore === 'number'
    && typeof damageAmount === 'number'
    && damageAmount > 0
    && (resolvedAfter == null || resolvedAfter >= healthBefore)
  ) {
    resolvedAfter = Math.max(0, healthBefore - damageAmount);
  }
  return resolvedAfter;
}

function getLiveUnitHealthAtSlot(gameState, viewerUserId, opponentId, zoneName, index, targetInstanceId) {
  if (!gameState?.state || zoneName == null || index == null || !targetInstanceId) {
    return undefined;
  }
  const zone = zoneName.includes('_front') ? 'front' : zoneName.includes('_flank') ? 'flank' : null;
  if (!zone) {
    return undefined;
  }
  const playerId = zoneName.startsWith('your_') ? viewerUserId : opponentId;
  if (!playerId) {
    return undefined;
  }
  const unit = gameState.state[playerId]?.playedOrders?.[zone]?.[index];
  if (unit?.instanceId === targetInstanceId && typeof unit.health === 'number') {
    return unit.health;
  }
  if (!unit || unit.instanceId !== targetInstanceId) {
    return 0;
  }
  return undefined;
}

/**
 * Authoritative post-hit HP for survivor countdown (uses live slot state when available).
 */
export function resolveCountdownTargetHealth(trigger, gameState, viewerUserId, opponentId) {
  if (!trigger?.targetInstanceId || typeof trigger.healthBefore !== 'number') {
    return trigger?.healthAfter;
  }

  let liveHealth;
  const { targetZone, targetIndex } = trigger;
  if (targetZone != null && targetIndex != null) {
    liveHealth = getLiveUnitHealthAtSlot(
      gameState,
      viewerUserId,
      opponentId,
      targetZone,
      targetIndex,
      trigger.targetInstanceId
    );
  }

  return resolveSurvivorHealthAfter({
    healthBefore: trigger.healthBefore,
    healthAfter: typeof trigger.healthAfter === 'number' ? trigger.healthAfter : undefined,
    damageAmount: typeof trigger.damageAmount === 'number' ? trigger.damageAmount : undefined,
    liveHealth,
  });
}

/**
 * Ghost snapshots are for slot visibility during projectiles — never replace authoritative HP on live units.
 */
export function pickBattlefieldUnitForDisplay(actualOrder, ghostUnit) {
  if (!actualOrder) {
    return ghostUnit ?? null;
  }
  return actualOrder;
}
